import { useState, useCallback } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'

export const TextArea = ({ value, onChange, rows }) => (
  <Box
    sx={{
      '& textarea': {
        backgroundColor: 'background.paper',
        width: '100%',
        height: '100%',
        display: 'block',
        resize: 'none',
        border: 0,
        p: 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'divider',
        borderRadius: '8px',
        '&:focus': {
          borderColor: 'white',
        },
      },
    }}
  >
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows || 4}
    />
  </Box>
)

export const Header = ({ text, mt = 4 }) => (
  <Box sx={{ fontWeight: 700, mb: 1, mt, fontStyle: 'italic' }}>{text}</Box>
)

export const Text = ({ children }) => (
  <Box sx={{ fontSize: '0.9em', mt: 1, mb: 2 }}>{children}</Box>
)

export const ExpandableTitles = ({
  titles,
  fontSize = '0.9em',
  accentColor,
  expanded: controlledExpanded,
  onToggle,
}) => {
  const [localExpanded, setLocalExpanded] = useState(false)
  const isControlled = controlledExpanded !== undefined
  const expanded = isControlled ? controlledExpanded : localExpanded
  const toggle = isControlled ? onToggle : () => setLocalExpanded((v) => !v)

  if (!titles?.length) return null

  return (
    <Box>
      <Box sx={{ fontSize, fontWeight: 700, mb: 0.3 }}>
        {accentColor && (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: accentColor,
              mr: 0.75,
              verticalAlign: 'middle',
            }}
          />
        )}
        {titles[0]}
      </Box>
      {titles.length > 1 && (
        <>
          {expanded &&
            titles.slice(1).map((t, i) => (
              <Box
                key={i + 1}
                sx={{ fontSize, color: 'text.secondary', mb: 0.3 }}
              >
                {i + 2}. {t}
              </Box>
            ))}
          <Box
            component="span"
            onClick={toggle}
            sx={{
              fontSize: '0.78em',
              color: 'text.secondary',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {expanded ? 'hide' : `+${titles.length - 1} more`}
          </Box>
        </>
      )}
    </Box>
  )
}

export const ExpandableTranscript = ({
  transcript,
  label,
  fontSize = '0.82em',
  expanded: controlledExpanded,
  onToggle,
}) => {
  const [localExpanded, setLocalExpanded] = useState(false)
  const isControlled = controlledExpanded !== undefined
  const expanded = isControlled ? controlledExpanded : localExpanded
  const toggle = isControlled ? onToggle : () => setLocalExpanded((v) => !v)

  if (!transcript) return null

  const showLabel = label || 'transcript'

  return (
    <Box>
      <Box
        component="span"
        onClick={toggle}
        sx={{
          fontSize: '0.82em',
          color: 'text.secondary',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {expanded ? `Hide ${showLabel}` : `Show ${showLabel}`}
      </Box>
      {expanded && (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            backgroundColor: 'background.paper',
            borderRadius: '8px',
            fontSize,
            whiteSpace: 'pre-wrap',
            maxHeight: 300,
            overflow: 'auto',
            color: 'text.secondary',
            lineHeight: 1.6,
          }}
        >
          {transcript}
        </Box>
      )}
    </Box>
  )
}

export const ExpandableText = ({
  text,
  maxLength = 60,
  fontSize = '0.82em',
  expanded: controlledExpanded,
  onToggle,
}) => {
  const [localExpanded, setLocalExpanded] = useState(false)
  const isControlled = controlledExpanded !== undefined
  const expanded = isControlled ? controlledExpanded : localExpanded
  const toggle = isControlled ? onToggle : () => setLocalExpanded((v) => !v)

  if (!text) return null

  const needsTruncation = text.length > maxLength

  return (
    <Box
      component="span"
      onClick={needsTruncation ? toggle : undefined}
      sx={{
        fontStyle: 'italic',
        fontSize,
        cursor: needsTruncation ? 'pointer' : 'default',
      }}
    >
      &ldquo;
      {expanded || !needsTruncation
        ? text
        : `${text.substring(0, maxLength)}...`}
      &rdquo;
    </Box>
  )
}

const fmtMoment = (m) => {
  const line1 = `#${m.rank} [${m.start_s.toFixed(1)}s–${m.end_s.toFixed(
    1
  )}s] (${(m.end_s - m.start_s).toFixed(1)}s) "${m.transcript_slice}" — ${
    m.why
  }`
  if (m.model_start_timestamp == null && m.model_end_timestamp == null)
    return line1
  const startTs =
    m.model_start_timestamp != null ? m.model_start_timestamp.toFixed(1) : '?'
  const endTs =
    m.model_end_timestamp != null ? m.model_end_timestamp.toFixed(1) : '?'
  const delta =
    m.model_end_timestamp != null
      ? (m.end_s - m.model_end_timestamp).toFixed(1)
      : '?'
  const endWord = m.end_word_text || '?'
  const detected = m.end_boundary_mode || 'uncertain'
  const line2 = `   start: model_ts=${startTs} → ${m.start_s.toFixed(
    1
  )} | end: model_ts=${endTs} → ${m.end_s.toFixed(
    1
  )} (Δ+${delta}s) end_word="${endWord}" [${detected}]`
  return `${line1}\n${line2}`
}

export const fmtMomentVerbose = (m) => {
  const lines = [
    `#${m.rank} [${m.start_s.toFixed(1)}s–${m.end_s.toFixed(1)}s] (${(
      m.end_s - m.start_s
    ).toFixed(1)}s)`,
    `   transcript: "${m.transcript_slice}"`,
    `   why: "${m.why}"`,
    `   start_word_index: ${m.start_word_index} → "${
      m.start_word_text
    }" @${m.start_word_start?.toFixed(1)}s (end=${m.start_word_end?.toFixed(
      2
    )})`,
    `   end_word_index:   ${m.end_word_index} → "${
      m.end_word_text
    }" @${m.end_word_start?.toFixed(1)}s (end=${m.end_word_end?.toFixed(2)})`,
  ]
  if (m.next_word_text != null) {
    lines.push(
      `   next_word:        "${m.next_word_text}" @${m.next_word_start?.toFixed(
        1
      )}s`
    )
  }
  if (m.last_included_word_text) {
    lines.push(
      `   last_included:    "${
        m.last_included_word_text
      }" (end=${m.last_included_word_end?.toFixed(2)})`
    )
  }
  lines.push(`   detected:         ${m.end_boundary_mode || 'uncertain'}`)
  const startTs =
    m.model_start_timestamp != null ? m.model_start_timestamp.toFixed(1) : '?'
  const endTs =
    m.model_end_timestamp != null ? m.model_end_timestamp.toFixed(1) : '?'
  lines.push(`   model timestamps: start=${startTs}  end=${endTs}`)
  lines.push(
    `   system output:    start=${m.start_s.toFixed(1)}  end=${m.end_s.toFixed(
      1
    )}`
  )
  if (m.model_end_timestamp != null) {
    const delta = (m.end_s - m.model_end_timestamp).toFixed(2)
    lines.push(`   Δ end: +${delta}s`)
  }
  return lines.join('\n')
}

const fmtSide = (label, r, { includeTranscript = true } = {}) => {
  if (!r) return ''
  const lines = [
    `### ${label}`,
    `Model: ${r.model_used || 'default'} | ${(r.latency_ms / 1000).toFixed(
      1
    )}s latency`,
    `Tokens: ${r.prompt_tokens} prompt, ${r.completion_tokens} completion, ${r.thinking_tokens}/${r.thinking_budget} thinking`,
    `Words: ${r.word_count} | Duration: ${r.duration_s}s`,
  ]
  if (r.auto_emojis?.length) lines.push(`Emojis: ${r.auto_emojis.join(' ')}`)
  if (r.best_quote) lines.push(`Best quote: "${r.best_quote}"`)
  if (r.animation_context)
    lines.push(`Animation context: ${r.animation_context}`)
  if (r.titles?.length) lines.push(`Titles: ${r.titles.join(' | ')}`)
  r.moments?.forEach((m) => lines.push(fmtMoment(m)))
  if (includeTranscript && r.indexed_transcript) {
    lines.push('', '#### Indexed Transcript', r.indexed_transcript)
  }
  return lines.join('\n')
}

export const formatTestExport = (result) => {
  if (!result) return ''
  return `## Auto Trim Test — ${result.source_id || 'unknown'}\n${fmtSide(
    'Result',
    result
  )}`
}

export const formatCompareExport = (sourceId, resultA, resultB) => {
  if (!resultA || !resultB) return ''
  const header = `## Auto Trim Compare — ${sourceId}`
  const topA = resultA.moments?.[0]
  const topB = resultB.moments?.[0]
  const samePick =
    topA &&
    topB &&
    Math.abs(topA.start_s - topB.start_s) <= 1 &&
    Math.abs(topA.end_s - topB.end_s) <= 1
  const verdict = samePick ? 'Same #1 pick' : 'Different #1 picks'
  return [
    header,
    `**${verdict}**`,
    '',
    fmtSide('Side A', resultA, { includeTranscript: false }),
    '',
    fmtSide('Side B', resultB, { includeTranscript: false }),
    '',
    '#### Indexed Transcript',
    resultA.indexed_transcript || resultB.indexed_transcript || '',
  ].join('\n')
}

export const formatBatchExport = (clips) => {
  const completed = clips.filter((c) => c.resultA && c.resultB)
  if (completed.length === 0) return ''
  const avg = (fn) =>
    completed.reduce((s, c) => s + fn(c), 0) / completed.length

  let diffCount = 0
  completed.forEach((c) => {
    const a = c.resultA.moments?.[0]
    const b = c.resultB.moments?.[0]
    if (
      a &&
      b &&
      (Math.abs(a.start_s - b.start_s) > 1 || Math.abs(a.end_s - b.end_s) > 1)
    )
      diffCount++
  })

  const lines = [
    `## Auto Trim Batch — ${completed.length} clips`,
    '',
    '### Aggregate',
    `| | A | B |`,
    `|---|---|---|`,
    `| Model | ${completed[0].resultA.model_used || 'default'} | ${
      completed[0].resultB.model_used || 'default'
    } |`,
    `| Avg latency | ${(avg((c) => c.resultA.latency_ms) / 1000).toFixed(
      1
    )}s | ${(avg((c) => c.resultB.latency_ms) / 1000).toFixed(1)}s |`,
    `| Avg tokens | ${Math.round(
      avg(
        (c) =>
          (c.resultA.prompt_tokens || 0) + (c.resultA.completion_tokens || 0)
      )
    )} | ${Math.round(
      avg(
        (c) =>
          (c.resultB.prompt_tokens || 0) + (c.resultB.completion_tokens || 0)
      )
    )} |`,
    `| Avg thinking | ${Math.round(
      avg((c) => c.resultA.thinking_tokens || 0)
    )} | ${Math.round(avg((c) => c.resultB.thinking_tokens || 0))} |`,
    `| #1 differs | ${diffCount}/${completed.length} |`,
  ]

  completed.forEach((c) => {
    const rA = c.resultA
    const rB = c.resultB
    const a = rA.moments?.[0]
    const b = rB.moments?.[0]
    const same =
      a &&
      b &&
      Math.abs(a.start_s - b.start_s) <= 1 &&
      Math.abs(a.end_s - b.end_s) <= 1

    lines.push(
      '',
      `---`,
      `### ${c.sourceId} (${rA.word_count}w, ${rA.duration_s}s) ${
        same ? '✓ same' : '⚡ differs'
      }`
    )

    lines.push('**A:**')
    if (rA.auto_emojis?.length)
      lines.push(`Emojis: ${rA.auto_emojis.join(' ')}`)
    if (rA.best_quote) lines.push(`Quote: "${rA.best_quote}"`)
    if (rA.animation_context)
      lines.push(`Animation context: ${rA.animation_context}`)
    if (rA.titles?.length) lines.push(`Titles: ${rA.titles.join(' | ')}`)
    rA.moments?.forEach((m) => lines.push(fmtMoment(m)))

    lines.push('**B:**')
    if (rB.auto_emojis?.length)
      lines.push(`Emojis: ${rB.auto_emojis.join(' ')}`)
    if (rB.best_quote) lines.push(`Quote: "${rB.best_quote}"`)
    if (rB.animation_context)
      lines.push(`Animation context: ${rB.animation_context}`)
    if (rB.titles?.length) lines.push(`Titles: ${rB.titles.join(' | ')}`)
    rB.moments?.forEach((m) => lines.push(fmtMoment(m)))

    const transcript = rA.indexed_transcript || rB.indexed_transcript
    if (transcript) lines.push('', '**Indexed Transcript:**', transcript)
  })

  return lines.join('\n')
}

export const formatVerboseResultExport = (sourceId, result, label) => {
  if (!result) return ''
  const lines = [
    `### ${sourceId} — ${label || 'Result'}`,
    `Model: ${result.model_used || 'default'} | ${(
      result.latency_ms / 1000
    ).toFixed(1)}s latency`,
    `Words: ${result.word_count} | Duration: ${result.duration_s}s`,
  ]
  if (result.auto_emojis?.length)
    lines.push(`Emojis: ${result.auto_emojis.join(' ')}`)
  if (result.best_quote) lines.push(`Quote: "${result.best_quote}"`)
  if (result.animation_context)
    lines.push(`Animation context: ${result.animation_context}`)
  if (result.titles?.length) lines.push(`Titles: ${result.titles.join(' | ')}`)
  lines.push('')
  result.moments?.forEach((m) => {
    lines.push(fmtMomentVerbose(m))
    lines.push('')
  })
  return lines.join('\n')
}

export const CopyResultsButton = ({ getText, label = 'Copy Results' }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    const text = getText()
    if (!text || !navigator.clipboard) return
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [getText])

  return (
    <Button
      size="small"
      variant="outlined"
      onClick={handleCopy}
      startIcon={
        copied ? (
          <CheckIcon sx={{ fontSize: 16 }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        )
      }
      sx={{ fontSize: '0.75em', textTransform: 'none' }}
    >
      {copied ? 'Copied!' : label}
    </Button>
  )
}

export const CopyIconButton = ({ getText, title = 'Copy verbose' }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(
    (e) => {
      e.stopPropagation()
      const text = getText()
      if (!text || !navigator.clipboard) return
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
    },
    [getText]
  )

  return (
    <Box
      component="span"
      onClick={handleCopy}
      title={title}
      sx={{
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        ml: 0.5,
        color: copied ? 'success.main' : 'text.secondary',
        '&:hover': { color: copied ? 'success.main' : 'primary.main' },
      }}
    >
      {copied ? (
        <CheckIcon sx={{ fontSize: 14 }} />
      ) : (
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      )}
    </Box>
  )
}
